import type { Color } from "@core/colors";

/**
 * Rubbers Out — hand-authored level data.
 *
 * Each band sits at a `(row, col)` slot. `row` 0 is the bottom of the field;
 * higher rows are closer to the top. A band is "blocked" while any other
 * unreleased band lives in a higher row AND its x-projection overlaps within
 * BAND_WIDTH * 0.5 (see main.ts `isBandBlocked`).
 *
 * `cols` is the total number of columns the layout pretends to have — it
 * controls the horizontal spread (see main.ts `makeBand`). Keeping `cols`
 * the same for every band in a level guarantees pin-x alignment, which is
 * what we want for "blocked from above" semantics.
 *
 * `palette` lists the box colors. We pick a 4-color palette per level so
 * pieces of each band-color always have a matching collection box.
 *
 * Designer notes:
 *  - Voodoo "unloseable first 10" → L1 is 3 bands in a single stack and
 *    every band can be released top-down without thinking.
 *  - L5 is 6 bands spread over two columns with deliberate cross-blocks so
 *    the player has to think about order.
 */
export interface BandSpec {
  /** Row 0 is the bottom of the play area; higher row = higher on screen. */
  row: number;
  /** Column index (0 .. cols-1). Same x-projection rule used for blocking. */
  col: number;
  /** Visual + sort color. Must appear in `palette`. */
  color: Color;
}

export interface RubbersOutLevel {
  /** Total columns laid out across the field width (see main.ts). */
  cols: number;
  /** Box palette — one per box slot at the bottom (BOXES_COUNT = 4). */
  palette: Color[];
  /** Bands placed on the field. Length should be a multiple of 3 per color. */
  bands: BandSpec[];
}

// Level 1 — 3 bands stacked in a single column. Pure top-down release.
// 9 pieces × 3 = 27 → 9 per color → 3 boxes filled.
const L1: RubbersOutLevel = {
  cols: 1,
  palette: ["red", "blue", "yellow", "green"],
  bands: [
    { row: 0, col: 0, color: "red" },
    { row: 1, col: 0, color: "blue" },
    { row: 2, col: 0, color: "yellow" },
  ],
};

// Level 2 — 4 bands, two columns, no cross-blocking. Each stack solves
// independently top-down.
const L2: RubbersOutLevel = {
  cols: 2,
  palette: ["red", "blue", "yellow", "green"],
  bands: [
    { row: 0, col: 0, color: "red" },
    { row: 1, col: 0, color: "blue" },
    { row: 0, col: 1, color: "yellow" },
    { row: 1, col: 1, color: "green" },
  ],
};

// Level 3 — 5 bands. Single column with 3 stacked, plus 2 on a second column.
// Player must alternate columns; obvious move at every step.
const L3: RubbersOutLevel = {
  cols: 2,
  palette: ["red", "blue", "yellow", "green"],
  bands: [
    { row: 0, col: 0, color: "red" },
    { row: 1, col: 0, color: "blue" },
    { row: 2, col: 0, color: "yellow" },
    { row: 0, col: 1, color: "green" },
    { row: 1, col: 1, color: "red" },
  ],
};

// Level 4 — 6 bands across two columns, mixed colors so player has to plan
// which color-line empties first (3 of any color triggers a box flush).
const L4: RubbersOutLevel = {
  cols: 2,
  palette: ["red", "blue", "yellow", "green"],
  bands: [
    { row: 0, col: 0, color: "yellow" },
    { row: 1, col: 0, color: "red" },
    { row: 2, col: 0, color: "blue" },
    { row: 0, col: 1, color: "green" },
    { row: 1, col: 1, color: "yellow" },
    { row: 2, col: 1, color: "red" },
  ],
};

// Level 5 — 6 bands, two columns, three rows each. Designed so a naive
// "always pick the leftmost top-most" sweep still wins — but the colors are
// scrambled so the player has to mind which box is closest to flushing.
const L5: RubbersOutLevel = {
  cols: 2,
  palette: ["red", "blue", "yellow", "purple"],
  bands: [
    { row: 0, col: 0, color: "purple" },
    { row: 1, col: 0, color: "yellow" },
    { row: 2, col: 0, color: "red" },
    { row: 0, col: 1, color: "blue" },
    { row: 1, col: 1, color: "purple" },
    { row: 2, col: 1, color: "yellow" },
  ],
};

export const levels: RubbersOutLevel[] = [L1, L2, L3, L4, L5];
